import { useCallback, useEffect, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { SkeletonCard, SkeletonTable } from '../../components/ui/Skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table'
import * as newsletterApi from '../../services/newsletterAdminApi'
import type { NewsletterOverview, PlatformNewsletterRow } from '../../services/newsletterAdminApi'

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'subscribed', label: 'Active' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
] as const

const SEARCH_DEBOUNCE_MS = 350

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

function statusBadge(status: string) {
  const base =
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset'
  if (status === 'subscribed') {
    return (
      <span className={`${base} bg-emerald-500/15 text-emerald-800 ring-emerald-500/25 dark:text-emerald-400`}>
        Active
      </span>
    )
  }
  if (status === 'unsubscribed') {
    return (
      <span className={`${base} bg-muted text-muted-foreground ring-border`}>Unsubscribed</span>
    )
  }
  return <span className={`${base} bg-muted text-muted-foreground ring-border`}>{status}</span>
}

export default function NewsletterOverviewPage() {
  const [overview, setOverview] = useState<NewsletterOverview | null>(null)
  const [items, setItems] = useState<PlatformNewsletterRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [loading, setLoading] = useState(true)
  const [listLoading, setListLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]['value']>('')
  const [storeFilter, setStoreFilter] = useState<number | ''>('')

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim()
      setPage(1)
      setDebouncedQ(next)
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [search])

  const loadOverview = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await newsletterApi.fetchNewsletterOverview()
      setOverview(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load overview')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadList = useCallback(async () => {
    setListLoading(true)
    try {
      const data = await newsletterApi.fetchPlatformNewsletterSubscribers({
        page,
        limit,
        q: debouncedQ || undefined,
        status: statusFilter || undefined,
        storeId: storeFilter === '' ? undefined : storeFilter,
      })
      setItems(data.items)
      setTotal(data.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load subscribers')
    } finally {
      setListLoading(false)
    }
  }, [page, limit, debouncedQ, statusFilter, storeFilter])

  useEffect(() => {
    void loadOverview()
  }, [loadOverview])

  useEffect(() => {
    void loadList()
  }, [loadList])

  const stats = [
    { label: 'Total subscribers', value: overview?.totalSubscribers ?? 0 },
    { label: 'Stores with subscribers', value: overview?.totalStoresWithSubscribers ?? 0 },
    { label: 'Recent signups', value: overview?.recent?.length ?? 0 },
  ]

  const totalPages = Math.max(1, Math.ceil(total / limit) || 1)
  const from = total === 0 ? 0 : (page - 1) * limit + 1
  const to = Math.min(page * limit, total)
  const listInitialLoading = listLoading && items.length === 0
  const hasActiveFilters = debouncedQ !== '' || statusFilter !== '' || storeFilter !== ''

  function clearFilters() {
    setSearch('')
    setDebouncedQ('')
    setStatusFilter('')
    setStoreFilter('')
    setPage(1)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Newsletter</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Platform-wide newsletter subscriptions across all storefronts.
        </p>
      </div>

      {error ? (
        <p className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        {loading
          ? Array.from({ length: 3 }, (_, i) => <SkeletonCard key={i} />)
          : stats.map((s) => (
              <Card key={s.label} className="p-5">
                <p className="text-sm font-medium text-muted-foreground">{s.label}</p>
                <p className="mt-2 text-3xl font-semibold tracking-tight text-foreground">{s.value}</p>
              </Card>
            ))}
      </div>

      <Card>
        <CardHeader title="Top stores" description="By active subscriber count" />
        {loading ? (
          <SkeletonTable columns={3} rows={5} />
        ) : (
          <Table variant="plain">
            <TableHeader>
              <TableRow>
                <TableHead>Store</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead className="text-right">Subscribers</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(overview?.topStores ?? []).length === 0 ? (
                <TableRow>
                  <td colSpan={3} className="px-4 py-6 text-sm text-muted-foreground">
                    No subscribers yet.
                  </td>
                </TableRow>
              ) : (
                overview?.topStores.map((row) => (
                  <TableRow key={row.storeId}>
                    <TableCell className="font-medium">{row.storeName ?? `Store #${row.storeId}`}</TableCell>
                    <TableCell className="text-muted-foreground">{row.organisationName ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.subscriberCount}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Subscribers"
          description="Search and filter signups across all storefronts"
          action={
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                void loadOverview()
                void loadList()
              }}
            >
              Refresh
            </Button>
          }
        />

        <div className="mb-4 flex flex-col gap-4 rounded-2xl border border-border/80 bg-muted/15 p-4 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-[12rem] flex-1">
            <Input
              label="Search"
              type="search"
              placeholder="Email address"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
          </div>
          <label className="flex min-w-[10rem] flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</span>
            <select
              className="min-h-10 rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as (typeof STATUS_FILTERS)[number]['value'])
                setPage(1)
              }}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.value || 'all'} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-[12rem] flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Store</span>
            <select
              className="min-h-10 rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm disabled:opacity-60"
              value={storeFilter === '' ? '' : String(storeFilter)}
              disabled={loading}
              onChange={(e) => {
                const v = e.target.value
                setStoreFilter(v === '' ? '' : Number(v))
                setPage(1)
              }}
            >
              <option value="">All stores</option>
              {(overview?.topStores ?? []).map((row) => (
                <option key={row.storeId} value={row.storeId}>
                  {row.storeName ?? `Store #${row.storeId}`}
                </option>
              ))}
            </select>
          </label>
          {hasActiveFilters ? (
            <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : null}
        </div>

        {listInitialLoading ? (
          <SkeletonTable columns={6} rows={6} />
        ) : (
          <>
            <Table variant="plain">
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Subscribed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-foreground">
                        {hasActiveFilters ? 'No subscribers match your filters' : 'No subscribers yet'}
                      </p>
                      <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                        {hasActiveFilters
                          ? 'Try a different search term, status, or store.'
                          : 'Signups from storefront footers and forms will appear here.'}
                      </p>
                      {hasActiveFilters ? (
                        <Button type="button" variant="secondary" size="sm" className="mt-4" onClick={clearFilters}>
                          Clear filters
                        </Button>
                      ) : null}
                    </td>
                  </TableRow>
                ) : (
                  items.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.email}</TableCell>
                      <TableCell>{row.storeName ?? `Store #${row.storeId}`}</TableCell>
                      <TableCell className="text-muted-foreground">{row.organisationName ?? '—'}</TableCell>
                      <TableCell>{statusBadge(row.status)}</TableCell>
                      <TableCell className="capitalize text-muted-foreground">{row.source}</TableCell>
                      <TableCell className="text-muted-foreground">{formatWhen(row.subscribedAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {total > 0 ? (
              <div className="mt-4 flex flex-col gap-4 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing{' '}
                  <span className="tabular-nums font-medium text-foreground">
                    {from}–{to}
                  </span>{' '}
                  of <span className="tabular-nums font-medium text-foreground">{total}</span>
                  <span className="hidden sm:inline"> · </span>
                  <span className="block sm:inline">
                    Page <span className="tabular-nums font-medium text-foreground">{page}</span> of {totalPages}
                  </span>
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1 || listLoading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages || listLoading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  )
}
